/**
 * Widget d'onboarding affiché sur le dashboard
 * 
 * Ce composant utilise la configuration centralisée de onboardingConfig.ts
 * pour afficher les items de configuration et leurs liens.
 * 
 * @see src/config/onboardingConfig.ts
 * @see docs/ONBOARDING_CONFIGURATION.md
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  CheckCircle2,
  Circle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  X,
  Rocket,
  Building,
  UtensilsCrossed,
  Clock,
  Mail,
  Star,
  Bell,
  MapPin,
  Phone,
  Globe,
  Image,
  ArrowRight,
  PartyPopper,
  LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ConfigurationStatus } from '@/hooks/useConfigurationCheck';
import { 
  CONFIGURATION_ITEMS, 
  ConfigurationItem,
  getItemLink,
  getItemDescription,
  isItemComplete,
  getItemCount,
} from '@/config/onboardingConfig';
import confetti from 'canvas-confetti';

// Mapping des noms d'icônes vers les composants
const ICON_COMPONENTS: Record<ConfigurationItem['icon'], LucideIcon> = {
  Building,
  UtensilsCrossed,
  Clock,
  Rocket,
  Star,
  Bell,
  Mail,
  Phone,
  MapPin,
  Globe,
  Image,
};

interface OnboardingWidgetProps {
  status: ConfigurationStatus;
  onDismiss: () => void;
}

interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  isComplete: boolean;
  isCritical: boolean;
  link: string;
  icon: React.ReactNode;
}

/**
 * Convertit un ConfigurationItem en ChecklistItem pour l'affichage
 */
function mapConfigToChecklist(
  item: ConfigurationItem,
  status: ConfigurationStatus
): ChecklistItem {
  const IconComponent = ICON_COMPONENTS[item.icon];
  const complete = isItemComplete(item, status);
  const count = getItemCount(item, status);
  
  return {
    id: item.id,
    label: item.label,
    description: getItemDescription(item, complete, count),
    isComplete: complete,
    isCritical: item.category === 'critical',
    link: getItemLink(item),
    icon: <IconComponent className="h-4 w-4" />,
  };
}

export function OnboardingWidget({ status, onDismiss }: OnboardingWidgetProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasTriggeredConfetti = useRef(false);
  const isComplete = status.isReadyForProduction && status.recommendedIssuesCount === 0;

  // Générer les items de checklist depuis la configuration centralisée
  const allItems = useMemo(() => 
    CONFIGURATION_ITEMS.map(item => mapConfigToChecklist(item, status)),
    [status]
  );

  const criticalItems = useMemo(() => 
    allItems.filter(item => item.isCritical),
    [allItems]
  );

  const recommendedItems = useMemo(() => 
    allItems.filter(item => !item.isCritical),
    [allItems]
  );

  // Trigger confetti when configuration is complete
  useEffect(() => {
    if (isComplete && !hasTriggeredConfetti.current) {
      hasTriggeredConfetti.current = true;
      
      // Fire confetti from multiple angles
      const duration = 3000;
      const end = Date.now() + duration;

      const colors = ['#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9'];

      const frame = () => {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.6 },
          colors,
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.6 },
          colors,
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };

      // Initial burst
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors,
      });

      frame();
    }
  }, [isComplete]);

  const completedCount = allItems.filter(item => item.isComplete).length;
  const totalCount = allItems.length;
  const progressPercentage = Math.round((completedCount / totalCount) * 100);

  const incompleteCritical = criticalItems.filter(item => !item.isComplete);
  const incompleteRecommended = recommendedItems.filter(item => !item.isComplete);

  // If everything is complete, show a success message with celebration
  if (isComplete) {
    return (
      <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 dark:border-green-900 dark:from-green-950/30 dark:to-emerald-950/30 overflow-hidden relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(34,197,94,0.1),transparent_50%)]" />
        <CardContent className="py-5 relative">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full shadow-lg shadow-green-500/25 animate-pulse">
                <PartyPopper className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-lg text-green-800 dark:text-green-200 flex items-center gap-2">
                  🎉 Configuration complète !
                </p>
                <p className="text-sm text-green-600 dark:text-green-400">
                  Félicitations ! Votre établissement est prêt à recevoir des réservations.
                </p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onDismiss} className="shrink-0 hover:bg-green-100 dark:hover:bg-green-900/30">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn(
      "transition-all duration-200",
      status.criticalIssuesCount > 0 
        ? "border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20"
        : "border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20"
    )}>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <div className={cn(
                "p-2 rounded-full",
                status.criticalIssuesCount > 0
                  ? "bg-amber-100 dark:bg-amber-900/30"
                  : "bg-blue-100 dark:bg-blue-900/30"
              )}>
                {status.criticalIssuesCount > 0 ? (
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                ) : (
                  <Rocket className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <CardTitle className="text-base flex items-center gap-2">
                  {status.criticalIssuesCount > 0 ? (
                    <>Configuration requise</>
                  ) : (
                    <>Optimisez votre configuration</>
                  )}
                  {status.criticalIssuesCount > 0 && (
                    <Badge variant="destructive" className="ml-1">
                      {status.criticalIssuesCount} critique{status.criticalIssuesCount > 1 ? 's' : ''}
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2 mt-1">
                  <Progress value={progressPercentage} className="h-2 flex-1 max-w-[200px]" />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {completedCount}/{totalCount}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="icon" className="shrink-0">
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
              {status.isReadyForProduction && (
                <Button variant="ghost" size="icon" onClick={onDismiss} className="shrink-0">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="pt-2 space-y-4">
            {/* Critical items */}
            {incompleteCritical.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-destructive flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Obligatoire pour recevoir des réservations
                </p>
                <div className="space-y-1">
                  {incompleteCritical.map((item) => (
                    <ChecklistItemRow key={item.id} item={item} />
                  ))}
                </div>
              </div>
            )}

            {/* Recommended items */}
            {incompleteRecommended.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">
                  Recommandé
                </p>
                <div className="space-y-1">
                  {incompleteRecommended.map((item) => (
                    <ChecklistItemRow key={item.id} item={item} />
                  ))}
                </div>
              </div>
            )}

            {/* Completed items (collapsed) */}
            {completedCount > 0 && (
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                    {completedCount} étape{completedCount > 1 ? 's' : ''} complétée{completedCount > 1 ? 's' : ''}
                    <ChevronDown className="h-4 w-4 ml-auto" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2 space-y-1">
                  {allItems.filter(item => item.isComplete).map((item) => (
                    <ChecklistItemRow key={item.id} item={item} />
                  ))}
                </CollapsibleContent>
              </Collapsible>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

function ChecklistItemRow({ item }: { item: ChecklistItem }) {
  return (
    <Link
      to={item.link}
      className={cn(
        "flex items-center gap-3 p-2 rounded-lg transition-colors group",
        item.isComplete
          ? "bg-green-50/50 dark:bg-green-950/20"
          : "bg-background/50 hover:bg-background"
      )}
    >
      <div className={cn(
        "shrink-0",
        item.isComplete ? "text-green-600 dark:text-green-400" : "text-muted-foreground"
      )}>
        {item.isComplete ? (
          <CheckCircle2 className="h-5 w-5" />
        ) : (
          <Circle className="h-5 w-5" />
        )}
      </div>
      <div className={cn(
        "shrink-0 p-1.5 rounded-md",
        item.isComplete 
          ? "bg-green-100/50 dark:bg-green-900/20 text-green-600 dark:text-green-400"
          : item.isCritical
            ? "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400"
            : "bg-muted text-muted-foreground"
      )}>
        {item.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn(
          "text-sm font-medium",
          item.isComplete && "text-green-700 dark:text-green-300"
        )}>
          {item.label}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {item.description}
        </p>
      </div>
      {!item.isComplete && (
        <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
      )}
    </Link>
  );
}
